import { useContext, useEffect, useRef } from 'react';
import { useQc } from '../qc/context';
import { SessionContext } from '../session/context';
import { useLicense } from '../license/context';
import { ProLock, UpgradePrompt } from '../license/UpgradePrompt';
import { indexOfKey } from '../qc/cursor';
import { isFixable, qcLegend } from '../qc/engine';
import { ruleLabel, ruleRemedy } from '../qc/ruleLabels';
import './Inspector.css';

interface InspectorProps {
  /** DOM id, so the shell's narrow-viewport toggle can reference the panel. */
  id?: string;
  /** Extra class (e.g. the shell's `cf-inspector--open` drawer state). */
  className?: string;
  /** When set, render a desktop collapse control that hides the panel (cue-forge-2bn). */
  onCollapse?: () => void;
}

/**
 * Right-hand inspector panel (spec §8): the QC issue list with the review loop —
 * a "current issue" cursor (highlighted card + N-of-M readout), Prev/Next +
 * Fix & next stepping, and per-issue Fix. Presentational: every action is a QC
 * method; revealing a cue is delegated to the shell via the QC `navTarget`, so
 * the panel needs no nav context. The cue chip selects the issue (cursor + cue
 * reveal); document-level issues (cueIndex -1) have no chip.
 */
export function Inspector({ id, className, onCollapse }: InspectorProps = {}) {
  const {
    issues,
    accepted,
    accept,
    unaccept,
    hasRun,
    cursorKey,
    current,
    fixOne,
    fixAll,
    fixStatus,
    announce,
    stepNext,
    stepPrev,
    select,
    fixAndAdvance,
    exportReport,
  } = useQc();
  const { isPro } = useLicense();
  // The Run-QC control only mounts once a document is loaded, so a "Run QC…"
  // empty state is misleading when there is no doc yet (cue-forge-w28.16).
  // Read the session directly (not via the throwing hook) so the Inspector
  // still renders in isolated tests with no SessionProvider — there it falls
  // back to the doc-present copy, and in the app it always sits under one.
  const session = useContext(SessionContext);
  const hasDoc = session ? session.loaded !== null : true;
  const cursorIndex = indexOfKey(issues, cursorKey);
  const activeRef = useRef<HTMLLIElement>(null);

  // Keep the current card visible as the cursor steps through a long list.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [cursorKey]);

  const showToolbar = hasRun && issues.length > 0;
  const legend = hasRun && issues.length > 0 ? qcLegend(issues, accepted.length) : null;

  return (
    <aside
      id={id}
      className={`cf-inspector${className ? ` ${className}` : ''}`}
      aria-label="Inspector"
    >
      <div className="cf-inspector__titlebar">
        <h2 className="cf-inspector__title">Issues</h2>
        {onCollapse && (
          <button
            type="button"
            className="cf-inspector__collapse"
            aria-label="Hide issues panel"
            title="Hide issues panel"
            onClick={onCollapse}
          >
            ⇥
          </button>
        )}
      </div>

      {/* Single reconciled legend (cue-forge-n3e.3): the whole picture on one
          line, with the fix controls + result right below it and the list. */}
      {legend && <p className="cf-inspector__legend">{legend.text}</p>}

      {/* Fix-all sits with the list it acts on and the result it produces, not
          in the opposite (centre) panel. Replaced by guidance when nothing is
          auto-fixable, so the control is never a dead "Fix all (0)". */}
      {legend &&
        (legend.fixable > 0 ? (
          <button
            type="button"
            className="cf-btn cf-inspector__fixall"
            onClick={fixAll}
            aria-label={`Fix all ${legend.fixable}`}
          >
            Fix all ({legend.fixable})
          </button>
        ) : (
          <p className="cf-inspector__nofix">
            No auto-fixes for these — edit the flagged cues to resolve them.
          </p>
        ))}

      {/* Visible outcome line for sighted users: a fix can decline, and the
          click must be seen to register. NOT a live region — its message is
          folded into the single composed announcement below, so making this
          live too would stack two SR utterances per action (cue-forge-drt.18). */}
      <p className="cf-inspector__status">{fixStatus}</p>

      {/* THE single always-mounted live region for QC intent feedback
          (WCAG 4.1.3, cue-forge-drt.18). The provider composes each action's
          outcome AND the resulting cursor position into one string — "Fixed X
          on cue 3. Issue 4 of 12: Y." — so fix-and-advance (and run, accept,
          step: the cursor card is non-focusable) yields exactly ONE utterance.
          Invariant: no other live region may update in the same user action as
          this one. QcControls' region only fires on its early-return run guard
          (run() is not reached), and the toolbar/export regions belong to
          actions that never set QC state. Always mounted: a region mounted
          together with its message is never announced. */}
      <p className="cf-visually-hidden" role="status" aria-live="polite">
        {announce}
      </p>

      {showToolbar && (
        <div className="cf-inspector__step">
          <button
            type="button"
            className="cf-inspector__stepbtn"
            aria-label="Previous issue"
            onClick={stepPrev}
            disabled={cursorIndex === 0}
          >
            ‹ Prev
          </button>
          <span className="cf-inspector__stepcount">
            {cursorIndex >= 0
              ? `Issue ${cursorIndex + 1} of ${issues.length}`
              : `${issues.length} issues`}
          </span>
          <button
            type="button"
            className="cf-inspector__stepbtn"
            aria-label="Next issue"
            onClick={stepNext}
            disabled={cursorIndex === issues.length - 1}
          >
            Next ›
          </button>
          <button
            type="button"
            className="cf-inspector__stepbtn cf-inspector__fixnext"
            aria-label="Fix current issue and advance"
            onClick={fixAndAdvance}
            disabled={!(current && isFixable(current))}
          >
            Fix &amp; next
          </button>
        </div>
      )}

      {!hasRun && (
        <p className="cf-inspector__empty">
          {hasDoc ? 'Run QC to see issues.' : 'Load a subtitle file, then run QC.'}
        </p>
      )}
      {hasRun && issues.length === 0 && (
        <p className="cf-inspector__empty">
          {accepted.length > 0
            ? `No outstanding issues — ${accepted.length} accepted below.`
            : 'No issues found.'}
        </p>
      )}

      <ul className="cf-issues" role="list">
        {issues.map((issue, i) => {
          // Human headline + accessible names; the raw slug stays as a tooltip.
          const label = ruleLabel(issue.ruleId);
          const remedy = ruleRemedy(issue.ruleId);
          const isCurrent = i === cursorIndex;
          return (
            <li
              key={`${issue.ruleId}-${issue.cueIndex}-${i}`}
              ref={isCurrent ? activeRef : undefined}
              className={`cf-issue${isCurrent ? ' cf-issue--current' : ''}`}
              aria-current={isCurrent ? 'true' : undefined}
            >
              <div className="cf-issue__head">
                <span
                  className={`cf-issue__sev cf-issue__sev--${issue.severity}`}
                  aria-hidden="true"
                />
                <span className="cf-visually-hidden">
                  {issue.severity === 'error' ? 'Error' : 'Warning'}:
                </span>
                <span className="cf-issue__rule" title={issue.ruleId}>
                  {label}
                </span>
                {issue.cueIndex >= 0 && (
                  <button
                    type="button"
                    className="cf-issue__cue cf-issue__goto cf-mono"
                    aria-label={`Go to cue ${issue.cueIndex} (${label})`}
                    onClick={() => select(issue)}
                  >
                    #{issue.cueIndex}
                  </button>
                )}
              </div>
              <p className="cf-issue__msg">{issue.message}</p>
              {/* Manual remedy: tells the operator the concrete action to take
                  (split / shorten / reorder …). Shown for flag-only rules, and
                  for max-cps as a fallback when its CPS assist declines. */}
              {remedy && <p className="cf-issue__remedy">{remedy}</p>}
              <div className="cf-issue__actions">
                {isFixable(issue) ? (
                  <button
                    type="button"
                    className="cf-issue__fix"
                    // Distinguish the otherwise-identical "Fix" buttons for AT users.
                    aria-label={
                      issue.cueIndex >= 0 ? `Fix ${label} on cue ${issue.cueIndex}` : `Fix ${label}`
                    }
                    onClick={() => fixOne(issue)}
                  >
                    Fix
                  </button>
                ) : (
                  // No auto-fix: take the operator to the cue to edit by hand.
                  issue.cueIndex >= 0 && (
                    <button
                      type="button"
                      className="cf-issue__fix cf-issue__edit"
                      aria-label={`Edit cue ${issue.cueIndex} (${label})`}
                      onClick={() => select(issue)}
                    >
                      Edit cue
                    </button>
                  )
                )}
                {/* Accept an intentional over-spec cue (cue-forge-n3e.7): it
                    leaves the active list and stops counting, on every card —
                    even fixable ones, since you may want to KEEP it rather than
                    auto-shorten it. */}
                <button
                  type="button"
                  className="cf-issue__accept"
                  aria-label={`Accept ${label}${
                    issue.cueIndex >= 0 ? ` on cue ${issue.cueIndex}` : ''
                  } as intentional`}
                  title="Mark this issue as intentional and move it to Accepted"
                  onClick={() => accept(issue)}
                >
                  Accept
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Accepted false positives (cue-forge-n3e.7): an archive of issues the
          operator deemed intentional, excluded from the counts/cursor/grid above.
          Native <details> gives free keyboard toggle + a non-colour disclosure
          shape; collapsed by default and only rendered when non-empty. */}
      {accepted.length > 0 && (
        <details className="cf-accepted">
          <summary className="cf-accepted__summary">Accepted ({accepted.length})</summary>
          <p className="cf-accepted__note">Marked intentional — excluded from the counts above.</p>
          <ul className="cf-accepted__list" role="list">
            {accepted.map((issue, i) => {
              const label = ruleLabel(issue.ruleId);
              const where = issue.cueIndex >= 0 ? ` on cue ${issue.cueIndex}` : '';
              return (
                <li
                  key={`acc-${issue.ruleId}-${issue.cueIndex}-${i}`}
                  className="cf-accepted__item"
                >
                  <div className="cf-issue__head">
                    <span
                      className={`cf-issue__sev cf-issue__sev--${issue.severity}`}
                      aria-hidden="true"
                    />
                    <span className="cf-visually-hidden">
                      {issue.severity === 'error' ? 'Error' : 'Warning'}:
                    </span>
                    <span className="cf-issue__rule" title={issue.ruleId}>
                      {label}
                    </span>
                    {issue.cueIndex >= 0 && (
                      // Plain reference, not a goto: an accepted issue is absent
                      // from the active list, so `select` has nothing to land on.
                      <span className="cf-issue__cue cf-mono">#{issue.cueIndex}</span>
                    )}
                    <button
                      type="button"
                      className="cf-issue__restore"
                      aria-label={`Restore ${label}${where} to the issue list`}
                      onClick={() => unaccept(issue)}
                    >
                      Restore
                    </button>
                  </div>
                  <p className="cf-issue__msg">{issue.message}</p>
                </li>
              );
            })}
          </ul>
        </details>
      )}

      {/* Export the run as the billable proof-of-work (cue-forge-n3e.6) — the
          client-facing report is the cleanest "why pay" trigger, so it is a Pro
          gate (cue-forge-sdv.3). QC itself stays free; only the export is locked.
          Shown whenever QC has run — a clean pass is a report worth signing too. */}
      {hasRun && (
        <div className="cf-inspector__export" role="group" aria-labelledby="cf-export-label">
          <span className="cf-inspector__export-label" id="cf-export-label">
            Export report {!isPro && <ProLock />}
          </span>
          {/* The group label + visible button text disambiguate these, so no
              redundant per-button aria-label (keeps Label-in-Name exact). Locked
              (disabled) for free users; the inline prompt below routes to upgrade. */}
          <button
            type="button"
            className="cf-btn cf-btn--sm"
            disabled={!isPro}
            onClick={() => exportReport('csv')}
          >
            CSV
          </button>
          <button
            type="button"
            className="cf-btn cf-btn--sm"
            disabled={!isPro}
            onClick={() => exportReport('html')}
          >
            Printable HTML
          </button>
          {!isPro && <UpgradePrompt feature="QC report export" surface="qc-export" compact />}
        </div>
      )}
    </aside>
  );
}
